import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { adminApi } from "@/lib/api";

const FILTERS = [
  { key: "accent", label: "Accent", options: [["", "Any accent"], ["australian", "Australian"], ["new zealand", "New Zealand"], ["british", "British"], ["american", "American"], ["irish", "Irish"], ["scottish", "Scottish"], ["canadian", "Canadian"], ["south african", "South African"]] },
  { key: "gender", label: "Gender", options: [["", "Any gender"], ["male", "Male"], ["female", "Female"], ["neutral", "Neutral"]] },
  { key: "age", label: "Age", options: [["", "Any age"], ["young", "Young"], ["middle_aged", "Middle aged"], ["old", "Older"]] },
  { key: "use_case", label: "Style", options: [["", "Any style"], ["conversational", "Conversational"], ["narrative_story", "Storytelling"], ["informative_educational", "Informative"], ["social_media", "Social media"], ["entertainment_tv", "Entertainment"], ["advertisement", "Advertising"], ["characters_animation", "Characters"]] },
];

export const DEFAULT_VOICE_FILTERS = { search: "", accent: "australian", gender: "male", age: "", use_case: "" };

export function VoiceFilterBar({ filters, onChange }) {
  const [search, setSearch] = useState(filters.search);
  useEffect(() => {
    const t = setTimeout(() => search !== filters.search && onChange({ ...filters, search }), 400);
    return () => clearTimeout(t);
  }, [search, filters, onChange]);

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="voice-filter-bar">
      <div className="flex items-center gap-2 bg-[#0A0A0A] border border-neutral-800 rounded-full px-3 py-1.5 flex-1 min-w-[180px] focus-within:border-[#73bf44]">
        <MagnifyingGlass size={14} className="text-neutral-500" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search voices, e.g. warm, aussie, deep"
          className="bg-transparent outline-none text-xs text-white w-full placeholder:text-neutral-600"
          data-testid="voice-search-input"
        />
      </div>
      {FILTERS.map((f) => (
        <select
          key={f.key}
          value={filters[f.key]}
          onChange={(e) => onChange({ ...filters, [f.key]: e.target.value })}
          aria-label={f.label}
          data-testid={`voice-filter-${f.key}`}
          className="bg-[#0A0A0A] border border-neutral-800 rounded-full px-3 py-1.5 text-xs text-neutral-200 outline-none focus:border-[#73bf44]"
        >
          {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      ))}
    </div>
  );
}

// Loads ElevenLabs voices for the current filters, with "load more" paging.
export function useVoiceOptions(filters) {
  const [state, setState] = useState({ rows: null, page: 0, hasMore: false, loading: true, error: null });
  const reqRef = useRef(0);

  const fetchPage = async (page, append) => {
    const req = ++reqRef.current;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await adminApi.get("/admin/voice-options", { params: { ...filters, page } });
      if (req !== reqRef.current) return; // a newer search replaced this one
      setState((s) => ({
        rows: append ? [...(s.rows || []), ...data.library] : [...data.account, ...data.library],
        page, hasMore: data.has_more, loading: false, error: data.error || null,
      }));
    } catch {
      if (req !== reqRef.current) return;
      setState((s) => ({ ...s, loading: false, error: "Couldn't load voices from ElevenLabs" }));
    }
  };

  useEffect(() => { fetchPage(0, false); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, loadMore: () => fetchPage(state.page + 1, true) };
}
